// -----------------------------
// Timesheet Types
// -----------------------------
export interface PendingWorklog {
    project_id?: number;
    task_id?: number;
    work_date?: string;
    hours_logged?: number;
    status?: string;
    description?: string;
}

export interface PendingDeleteWorklog {
    awaitingIdentifier?: boolean;
}

export const startCreateTimesheetFlow = (
    setPendingWorklog: (data: PendingWorklog | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    projectIdFromIntent?: number,
    taskIdFromIntent?: number
) => {
    if (projectIdFromIntent && taskIdFromIntent) {
        setPendingWorklog({
            project_id: projectIdFromIntent,
            task_id: taskIdFromIntent,
        });

        pushAIChat("Got it. How many hours did you work?");
        speak("How many hours did you work?");
        return;
    }

    setPendingWorklog({});
    pushAIChat("Sure! Which project is this work for?");
    speak("Which project is this work for?");
};

export const processPendingTimesheet = (
    text: string,
    pendingWorklog: PendingWorklog,
    setPendingWorklog: (data: PendingWorklog | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — Project
    if (!pendingWorklog.project_id) {
        const projectId = Number(text);

        if (!projectId || isNaN(projectId)) {
            pushAIChat("Please provide a valid project ID.");
            speak("Please provide a valid project.");
            return;
        }

        setPendingWorklog({ ...pendingWorklog, project_id: projectId });
        pushAIChat("Great. Which task did you work on?");
        speak("Which task did you work on?");
        return;
    }

    // STEP 2 — Task
    if (!pendingWorklog.task_id) {
        const taskId = Number(text);

        if (!taskId || isNaN(taskId)) {
            pushAIChat("Please provide a valid task.");
            speak("Please provide a valid task.");
            return;
        }

        setPendingWorklog({ ...pendingWorklog, task_id: taskId });
        pushAIChat("How many hours did you work?");
        speak("How many hours did you work?");
        return;
    }

    // STEP 3 — Hours
    if (!pendingWorklog.hours_logged) {
        const hours = Number(text);

        if (!hours || isNaN(hours)) {
            pushAIChat("Please provide valid hours.");
            speak("Please provide valid hours.");
            return;
        }

        setPendingWorklog({ ...pendingWorklog, hours_logged: hours });
        pushAIChat("What is the status? Pending, In Progress, or Completed?");
        speak("What is the status?");
        return;
    }

    // STEP 4 — Status
    if (!pendingWorklog.status) {
        const status =
            text.toLowerCase().includes("complete") ? "Completed" :
                text.toLowerCase().includes("progress") ? "In Progress" :
                    "Pending";

        setPendingWorklog({ ...pendingWorklog, status });

        pushAIChat("Would you like to add a description?");
        speak("Would you like to add a description?");
        return;
    }

    // STEP 5 — Description + CREATE
    const description = text.toLowerCase().includes("no")
        ? ""
        : text;

    const finalPayload = {
        ...pendingWorklog,
        description,
        work_date: new Date().toISOString().split("T")[0],
    };

    pushAIChat("Perfect! Creating your worklog now.");
    speak("Creating your worklog now.");

    window.dispatchEvent(
        new CustomEvent("ai-create-worklog", {
            detail: finalPayload
        })
    );

    setPendingWorklog(null);
};

export const startDeleteTimesheetFlow = (
    setPendingDelete: (data: PendingDeleteWorklog | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPendingDelete({ awaitingIdentifier: true });

    pushAIChat("Sure. Please tell me the work date or worklog ID to delete.");
    speak("Please tell me the work date or worklog ID.");
};

export const processPendingDeleteTimesheet = (
    text: string,
    pendingDelete: PendingDeleteWorklog,
    setPendingDelete: (data: PendingDeleteWorklog | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pendingDelete.awaitingIdentifier) return;

    const cleanText = text.trim();

    // Try ID first
    const possibleId = Number(cleanText);

    const detail =
        !isNaN(possibleId)
            ? { work_id: possibleId }
            : { work_date: cleanText };

    pushAIChat("Deleting the worklog now.");
    speak("Deleting the worklog now.");

    window.dispatchEvent(
        new CustomEvent("ai-delete-worklog", {
            detail
        })
    );

    setPendingDelete(null);
};
